// src/utils/ws.js
import { getAccessToken, refreshAccessToken, getRefreshToken } from './api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

function getWsUrl() {
    const base = API_BASE_URL.replace(/^http/, 'ws');
    return `${base}/v2/ws`;
}

const WS_STATES = {
    DISCONNECTED: 'disconnected',
    CONNECTING: 'connecting',
    AUTHENTICATING: 'authenticating',
    CONNECTED: 'connected',
};

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000];

export class AgentWebSocket {
    constructor() {
        this._ws = null;
        this._state = WS_STATES.DISCONNECTED;
        this._reconnectAttempt = 0;
        this._reconnectTimer = null;
        this._intentionalClose = false;
        this._currentSessionId = null;
        this._lastEventIds = {};
        this._pendingAuth = null;
        this._pendingMessages = [];

        this.onStateChange = null;
        this.onAuthOk = null;
        this.onSubscribed = null;
        this.onEvent = null;
        this.onControlMessage = null;
        this.onError = null;
        this.onDisconnect = null;
        this.onBinaryMessage = null;
        this.onVoiceEvent = null;
    }

    get state() {
        return this._state;
    }

    get isConnected() {
        return this._state === WS_STATES.CONNECTED;
    }

    get currentSessionId() {
        return this._currentSessionId;
    }

    connect() {
        if (this._ws && (this._ws.readyState === WebSocket.OPEN || this._ws.readyState === WebSocket.CONNECTING)) {
            return;
        }

        this._intentionalClose = false;
        this._setState(WS_STATES.CONNECTING);

        const token = getAccessToken();
        if (!token) {
            this._setState(WS_STATES.DISCONNECTED);
            this.onError?.({ code: 'NO_TOKEN', message: 'Not authenticated' });
            return;
        }

        try {
            this._ws = new WebSocket(getWsUrl());
            this._ws.binaryType = 'arraybuffer';
        } catch (e) {
            this._setState(WS_STATES.DISCONNECTED);
            this._scheduleReconnect();
            return;
        }

        this._ws.onopen = () => {
            this._setState(WS_STATES.AUTHENTICATING);
            this._send({ type: 'auth', token });
        };

        this._ws.onmessage = (e) => {
            if (e.data instanceof ArrayBuffer) {
                this.onBinaryMessage?.(e.data);
                return;
            }
            let msg;
            try {
                msg = JSON.parse(e.data);
            } catch {
                return;
            }
            this._handleMessage(msg);
        };

        this._ws.onclose = (e) => {
            const wasConnected = this._state === WS_STATES.CONNECTED;
            this._setState(WS_STATES.DISCONNECTED);
            this._ws = null;

            if (e.code === 4003) {
                this._handleTokenRefreshAndReconnect();
                return;
            }

            this.onDisconnect?.({ code: e.code, reason: e.reason, wasConnected });

            if (!this._intentionalClose) {
                this._scheduleReconnect();
            }
        };

        this._ws.onerror = () => {
            // onclose will fire after this
        };
    }

    disconnect() {
        this._intentionalClose = true;
        this._clearReconnect();
        if (this._ws) {
            this._ws.close(1000, 'Client disconnect');
            this._ws = null;
        }
        this._setState(WS_STATES.DISCONNECTED);
        this._currentSessionId = null;
    }

    chat({ message, sessionId = null, shareId = null, model = null, agentId = null, contentUrls = null, agentConfig = null, source = null, source_id = null, contexts = null }) {
        this._send({
            type: 'chat',
            message,
            session_id: sessionId || undefined,
            share_id: shareId || undefined,
            model: model || undefined,
            agent_id: agentId || undefined,
            content_urls: contentUrls || undefined,
            agent_config: agentConfig || undefined,
            source: source || undefined,
            source_id: source_id || undefined,
            contexts: contexts && contexts.length ? contexts : undefined,
        });
    }

    subscribe(sessionId, lastEventId = null) {
        this._send({
            type: 'subscribe',
            session_id: sessionId,
            last_event_id: lastEventId,
        });
    }

    stop(sessionId) {
        this._send({
            type: 'stop',
            session_id: sessionId,
        });
    }

    pushMessage(sessionId, message) {
        this._send({
            type: 'push_message',
            session_id: sessionId,
            message,
        });
    }

    sendApproval({ sessionId, approvalKey, decisions = null, answers = null, declined = false }) {
        const msg = {
            type: 'approval',
            session_id: sessionId,
            approval_key: approvalKey,
        };
        if (decisions) msg.decisions = decisions;
        if (answers) msg.answers = answers;
        if (declined) msg.declined = true;
        this._send(msg);
    }

    send(data) {
        this._send(data);
    }

    sendBinary(data) {
        if (this._ws?.readyState === WebSocket.OPEN) {
            this._ws.send(data);
        }
    }

    voiceAgentStart({ sessionId = null, shareId = null, model = null, language = 'auto', voiceId = null } = {}) {
        this._send({
            type: 'voice_agent_start',
            session_id: sessionId || undefined,
            share_id: shareId || undefined,
            model: model || undefined,
            language,
            voice_id: voiceId || undefined,
        });
    }

    voiceAgentStop() {
        this._send({ type: 'voice_agent_stop' });
    }

    voiceAgentEndTurn() {
        this._send({ type: 'voice_agent_end_turn' });
    }

    voiceAgentInterrupt() {
        this._send({ type: 'voice_agent_interrupt' });
    }

    sttStart({ language = 'auto' } = {}) {
        this._send({ type: 'stt_start', language });
    }

    sttStop() {
        this._send({ type: 'stt_stop' });
    }

    getLastEventId(sessionId) {
        return this._lastEventIds[sessionId] || null;
    }

    clearLastEventId(sessionId) {
        delete this._lastEventIds[sessionId];
    }

    _send(data) {
        if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
            if (data.type !== 'auth' && data.type !== 'pong') {
                console.log('[WS] Queued pending message:', data.type);
                this._pendingMessages.push(data);
            }
            return false;
        }
        this._ws.send(JSON.stringify(data));
        return true;
    }

    _flushPendingMessages() {
        const pending = this._pendingMessages.splice(0);
        for (const msg of pending) {
            console.log('[WS] Sending queued message:', msg.type);
            this._send(msg);
        }
    }

    _handleMessage(msg) {
        if (msg.event_id) {
            const sid = msg.session_id || this._currentSessionId;
            if (sid) {
                this._lastEventIds[sid] = msg.event_id;
            }
            this.onEvent?.(msg);
            return;
        }

        switch (msg.type) {
            case 'auth_ok':
                this._setState(WS_STATES.CONNECTED);
                this._reconnectAttempt = 0;
                this._flushPendingMessages();
                this.onAuthOk?.(msg);
                break;

            case 'subscribed':
                this._currentSessionId = msg.session_id;
                this.onSubscribed?.(msg);
                break;

            case 'hub_delivered':
                this.onHubDelivered?.(msg);
                break;

            case 'message_queued':
                this.onControlMessage?.(msg);
                break;

            case 'state_reset':
                this.onControlMessage?.(msg);
                break;

            case 'state_sync':
            case 'session_created':
            case 'session_renamed':
                this.onEvent?.(msg);
                break;

            case 'voice_agent_ready':
            case 'stt_ready':
            case 'stt_chunk':
            case 'stt_output':
            case 'tts_start':
            case 'tts_end':
            case 'no_speech_timeout':
            case 'silence_timeout':
                this.onVoiceEvent?.(msg);
                break;

            case 'error':
                this.onError?.(msg);
                break;

            case 'ping':
                this._send({ type: 'pong' });
                break;

            case 'pong':
                break;

            default:
                this.onControlMessage?.(msg);
                break;
        }
    }

    _setState(state) {
        if (this._state === state) return;
        this._state = state;
        this.onStateChange?.(state);
    }

    _scheduleReconnect() {
        this._clearReconnect();
        const delay = RECONNECT_DELAYS[Math.min(this._reconnectAttempt, RECONNECT_DELAYS.length - 1)];
        this._reconnectAttempt++;
        this._reconnectTimer = setTimeout(() => {
            this.connect();
        }, delay);
    }

    _clearReconnect() {
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
    }

    async _handleTokenRefreshAndReconnect() {
        if (!getRefreshToken()) {
            this.onError?.({ code: 'AUTH_FAILED', message: 'No refresh token' });
            return;
        }
        try {
            await refreshAccessToken();
            this._reconnectAttempt = 0;
            this.connect();
        } catch {
            this.onError?.({ code: 'AUTH_FAILED', message: 'Token refresh failed' });
        }
    }
}

let _instance = null;

export function getAgentWS() {
    if (!_instance) {
        _instance = new AgentWebSocket();
    }
    return _instance;
}

export function destroyAgentWS() {
    if (_instance) {
        _instance.disconnect();
        _instance = null;
    }
}

export { WS_STATES };
